import React, { useState } from "react";
import { Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import Call from "@/model/interface/Call";
import { Button } from "@/components/ui/button";
import { useLazyGetCallRecordingUrlQuery, useMarkCallRecordingReviewedMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { formatDuration } from "@/util/call";

interface VoicemailPlayerProps {
  call: Call;
}

const VoicemailPlayer: React.FC<VoicemailPlayerProps> = ({ call }) => {
  const [getCallRecordingUrl, { isFetching }] = useLazyGetCallRecordingUrlQuery();
  const [markCallRecordingReviewed] = useMarkCallRecordingReviewedMutation();
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const reviewed = !!call.recording?.reviewedAt;

  const handleListen = async () => {
    if (!call.id) {
      return;
    }
    if (url) {
      setOpen((current) => !current);
      return;
    }
    try {
      const response = await getCallRecordingUrl(call.id).unwrap();
      setUrl(response.url);
      setOpen(true);
      if (!reviewed) {
        void markCallRecordingReviewed(call.id);
      }
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to load voicemail"));
    }
  };

  return (
    <div className="space-y-2">
      {call.recording?.transcription && (
        <p className="text-xs text-muted-foreground line-clamp-2">{call.recording.transcription}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => void handleListen()} disabled={isFetching}>
          {isFetching ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="mr-1.5 h-3.5 w-3.5" />
          )}
          {open ? "Hide player" : "Listen"}
        </Button>
        {call.recording?.duration != null && (
          <span className="text-xs text-muted-foreground tabular-nums">{formatDuration(call.recording.duration)}</span>
        )}
        {reviewed ? (
          <span className="text-xs text-muted-foreground">Reviewed</span>
        ) : (
          <span className="status-pill-info">New</span>
        )}
      </div>
      {open && url && <audio controls autoPlay className="h-10 w-full max-w-md" src={url} />}
    </div>
  );
};

export default VoicemailPlayer;
